import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { CheckCircle, Error, Info } from '@mui/icons-material';

interface CarveOutUploadProgressProps {
  open: boolean;
  progress: number;
  status: string;
  error?: string;
  success?: boolean;
  uploadCount?: number;
  totalCount?: number;
  onClose: () => void;
}

const CarveOutUploadProgress: React.FC<CarveOutUploadProgressProps> = ({
  open,
  progress,
  status,
  error,
  success = false,
  uploadCount = 0,
  totalCount = 0,
  onClose,
}) => {
  const getStatusIcon = () => {
    if (error) return <Error color="error" />;
    if (success) return <CheckCircle color="success" />;
    return <Info color="info" />;
  };

  const getStatusColor = () => {
    if (error) return 'error';
    if (success) return 'success';
    return 'info';
  };

  return (
    <Dialog open={open} onClose={success || error ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getStatusIcon()}
          CarveOut Upload Progress
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Progress Bar */}
          <Box>
            <Typography variant="body2" gutterBottom>
              Progress: {Math.round(progress)}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              color={getStatusColor() as any}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Status Message */}
          <Alert severity={getStatusColor() as any} variant="outlined">
            {status}
          </Alert>

          {/* Upload Statistics */}
          {totalCount > 0 && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1, 
              border: '1px solid', 
              borderColor: 'grey.200' 
            }}>
              <Typography variant="subtitle2" gutterBottom>
                Upload Statistics
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText 
                    primary="Total Records" 
                    secondary={totalCount.toLocaleString()} 
                  />
                </ListItem>
                <ListItem sx={{ py: 0 }}>
                  <ListItemText 
                    primary="Processed" 
                    secondary={uploadCount.toLocaleString()} 
                  />
                </ListItem>
                {success && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText 
                      primary="Success Rate" 
                      secondary={`${totalCount > 0 ? Math.round((uploadCount / totalCount) * 100) : 0}%`} 
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {/* Error Details */}
          {error && (
            <Alert severity="error">
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
          )}

          {/* Success Message */}
          {success && !error && (
            <Alert severity="success">
              <Typography variant="body2">
                CarveOut upload completed successfully! All records have been processed and imported.
              </Typography>
            </Alert>
          )}

          {/* In Progress Message */}
          {!success && !error && progress < 100 && (
            <Typography variant="body2" color="text.secondary">
              Please wait while your CarveOut data is being processed. Do not close this dialog.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          variant={success || error ? "contained" : "outlined"}
          disabled={!success && !error && progress < 100}
        >
          {success || error ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarveOutUploadProgress;